import { isToolUIPart } from 'ai';
import { RachelUIMessage } from '../../../../rachel.types';
import { getAskUserAnswerEntries } from '../../../../rachelAskUser.utils';
import { useRachelStore } from '../../../../store/rachel.store';
import { selectIsMessageInterrupted } from '../../../../store/rachel.store.selectors';
import { getWebVisualizationBlocks } from '../../../RachelMessagePart/components/RachelVizPart/rachelVizPart.utils';
import { RachelReasoningStep } from '../../../RachelReasoningTrace/RachelReasoningTrace';
import { collectDocLinkBlock, withoutDocLinks } from './rachelAssistantMessage.utils';

type RachelPart = RachelUIMessage['parts'][number];

export interface ExecuteCodeOutputFile {
  nome: string;
  url: string;
}

const isOutputFile = (file: unknown): file is ExecuteCodeOutputFile =>
  typeof file === 'object' &&
  file !== null &&
  typeof (file as ExecuteCodeOutputFile).nome === 'string' &&
  typeof (file as ExecuteCodeOutputFile).url === 'string';

const getOutputFiles = (part: RachelPart): ExecuteCodeOutputFile[] => {
  if (!isToolUIPart(part) || part.state !== 'output-available') {
    return [];
  }

  const files = (part.output as { arquivosSaida?: unknown } | undefined)?.arquivosSaida;

  return Array.isArray(files) ? files.filter(isOutputFile) : [];
};

const getNarrationText = (part: RachelPart): string =>
  (part.type === 'text' || part.type === 'reasoning') && part.text.trim() ? part.text : '';

interface UseRachelAssistantMessageParams {
  message: RachelUIMessage;
  isLive: boolean;
}

export const useRachelAssistantMessage = ({ message, isLive }: UseRachelAssistantMessageParams) => {
  const { parts } = message;
  const isInterrupted = useRachelStore(selectIsMessageInterrupted(message.id));

  const lastToolIndex = parts.findLastIndex(isToolUIPart);
  const lastTextIndex = parts.findLastIndex((part) => part.type === 'text');
  const answerIndex = lastTextIndex > lastToolIndex ? lastTextIndex : -1;

  const narrations = parts.filter((part, index) => index !== answerIndex && getNarrationText(part));

  const steps: RachelReasoningStep[] = narrations.map((part, index) => ({
    id: `${message.id}-step-${index}`,
    text: getNarrationText(part),
    state: isLive && answerIndex < 0 && index === narrations.length - 1 ? 'now' : 'ok',
  }));

  const answerText = answerIndex >= 0 ? getNarrationText(parts[answerIndex]) : '';

  const hasAnswerStarted = !isLive || Boolean(answerText);

  const toolGroups = hasAnswerStarted
    ? parts
        .filter(isToolUIPart)
        .map((part) => ({ part, blocks: getWebVisualizationBlocks(part) }))
        .filter((group) => group.blocks.length > 0)
    : [];

  const docLinkBlock = collectDocLinkBlock(toolGroups.flatMap((group) => group.blocks));

  const vizGroups = toolGroups
    .map((group) => ({ ...group, blocks: withoutDocLinks(group.blocks) }))
    .filter((group) => group.blocks.length > 0);

  const outputFiles = parts.flatMap(getOutputFiles);

  const askUserAnswers = parts.flatMap(getAskUserAnswerEntries);

  const hasContent =
    steps.length > 0 ||
    vizGroups.length > 0 ||
    outputFiles.length > 0 ||
    askUserAnswers.length > 0 ||
    Boolean(docLinkBlock) ||
    Boolean(answerText);

  return {
    steps,
    vizGroups,
    outputFiles,
    askUserAnswers,
    docLinkBlock,
    answerText,
    hasContent,
    isInterrupted,
  };
};
